import React, { createContext, useContext, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { FaQuestionCircle } from 'react-icons/fa';

const topics = {
  grossMargin: { title: 'Marge brute HT', formula: 'Marge HT = ventes nettes HT − coût net HT', rules: [
    'Le coût figé est le coût d’achat HT enregistré sur la ligne du BL. Il reste identique même si le prix de la fiche change. Exemple : figé à 15 DH, il reste à 15 DH si le prix passe à 17 DH.',
    'Les retours sont déduits. Sans coût enregistré, le prix d’achat actuel est utilisé.',
    'Les estimations sont signalées. Si des coûts manquent, la marge est partielle, hors ajustements du document.',
  ] },
  net: { title: 'Ventes nettes TTC', formula: 'Ventes nettes = ventes livrées − retours validés', rules: [
    'Montants TTC des documents enregistrés sur la période.',
    'Panier moyen = ventes livrées ÷ nombre de ventes, avant retours.',
  ] },
  sales: { title: 'Ventes livrées', rules: [
    'BL livrés enregistrés sur la période, payés ou non.',
    'Le détail par article exclut les ajustements globaux du document.',
  ] },
  returns: { title: 'Retours clients validés', rules: [
    'Retours validés ou comptabilisés enregistrés sur la période.',
    'Ils sont affichés en positif ici, puis déduits des ventes et de la marge.',
  ] },
  purchases: { title: 'Réceptions validées', rules: [
    'Total TTC des réceptions validées dont la date est dans la période.',
    'Le détail inclut les quantités gratuites et présente les montants avant remise globale.',
  ] },
  outstanding: { title: 'Réceptions restant à payer', formula: 'Reste TTC = montant TTC − montant réglé', rules: [
    'Solde actuel des réceptions validées de la période, avec un minimum de zéro.',
    'Les règlements effectués après la période sont pris en compte.',
  ] },
  products: { title: 'Articles vendus, après retours', rules: [
    'Articles distincts de la période dont la quantité nette après retours est différente de zéro.',
    'Quantités et CA nets après retours. Les montants HT excluent les ajustements globaux.',
  ] },
  lowStock: { title: 'Stock faible', rules: [
    'Articles actifs dont le stock disponible est inférieur au seuil minimal.',
    'Stock actuel, indépendant de la période. Les stocks nuls ou négatifs sont inclus si sous le seuil.',
  ] },
  outOfStock: { title: 'Stock épuisé', rules: [
    'Articles actifs dont le stock disponible est nul ou négatif.',
    'Stock actuel, indépendant de la période sélectionnée.',
  ] },
  clients: { title: 'Ventes par client', rules: [
    'Ventes TTC moins retours TTC, regroupés par client sur la période.',
    'Les documents sans client sont regroupés sous Passager.',
  ] },
  categories: { title: 'Ventes par catégorie', rules: [
    'Quantités et CA nets HT après retours, regroupés par catégorie actuelle.',
    'Les ajustements globaux des documents sont exclus.',
  ] },
  margins: { title: 'Coût et marge estimés', formula: 'Marge estimée HT = CA net HT − coût estimé HT', rules: [
    'Coût estimé = quantité nette × prix d’achat actuel HT, hors ajustements globaux.',
    'Ce calcul peut différer de la marge brute fondée sur les coûts figés. Les coûts manquants rendent les totaux partiels.',
  ] },
  slowProducts: { title: 'Produits à faible rotation', rules: [
    'Articles actifs classés par quantité nette croissante, après retours.',
    'Les articles sans vente sont inclus avec une quantité nulle.',
  ] },
  days: { title: 'Ventes nettes par jour', rules: [
    'Ventes livrées TTC moins retours, par jour d’enregistrement.',
    'Cliquez sur une barre pour consulter les lignes du jour.',
  ] },
  marginDays: { title: 'Marge brute HT par jour', rules: [
    'CA net HT moins coût net HT, après retours, par jour d’enregistrement.',
    'Une interruption indique un jour avec des coûts manquants.',
  ] },
  months: { title: 'Ventes nettes par mois', rules: [
    'Ventes livrées TTC moins retours, regroupées par mois d’enregistrement.',
    'Seule la période sélectionnée est retenue.',
  ] },
  topProducts: { title: 'Top produits par quantité', rules: [
    'Les dix articles aux quantités nettes les plus élevées, après retours.',
    'En cas d’égalité, le CA net TTC départage les articles.',
  ] },
  categoryChart: { title: 'Ventes par catégorie', rules: [
    'CA net TTC des lignes après retours, par catégorie actuelle.',
    'Les valeurs négatives sont conservées ; les ajustements globaux sont exclus.',
  ] },
  categoryShare: { title: 'Répartition par catégorie', rules: [
    'Répartition du CA net TTC positif entre les catégories.',
    'Les catégories à CA nul ou négatif sont exclues du cercle.',
  ] },
};

const HelpContext = createContext(null);

export function DashboardHelpButton({ topic, className = '' }) {
  const open = useContext(HelpContext);
  if (!topics[topic]) return null;
  const title = topics[topic].title;
  return <button type="button" className={`dashboard-help-button ${className}`}
    title={`Aide : ${title}`} aria-label={`Aide : ${title}`} aria-haspopup="dialog"
    onClick={event => { event.preventDefault(); event.stopPropagation(); open?.(topic); }}>
    <FaQuestionCircle aria-hidden="true" />
  </button>;
}

export default function DashboardHelpProvider({ children }) {
  const [topic, setTopic] = useState(null);
  const current = topics[topic];
  const close = () => setTopic(null);
  return <HelpContext.Provider value={setTopic}>
    {children}
    <Modal isOpen={Boolean(current)} toggle={close} trapFocus labelledBy="dashboard-help-title" className="dashboard-help-modal">
      <ModalHeader id="dashboard-help-title" toggle={close}>{current?.title}</ModalHeader>
      <ModalBody>
        {current?.formula && <p className="dashboard-help-formula">{current.formula}</p>}
        <ul className="dashboard-help-rules">{current?.rules.map(rule => <li key={rule}>{rule}</li>)}</ul>
      </ModalBody>
      <ModalFooter><Button color="primary" onClick={close}>Fermer</Button></ModalFooter>
    </Modal>
  </HelpContext.Provider>;
}
